import { db } from "../firebase";
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    getDoc, 
    orderBy, 
    limit, 
    runTransaction, 
    serverTimestamp
} from "firebase/firestore";

/**
 * Calculates current closing stock based on a snapshot of inventory data from Firestore.
 * Supports various field names used across the system for inward/outward.
 */
export const calculateClosing = (details) => {
    if (!details) return 0;
    const opening = Number(details.opening || 0);
    const inward = Number(details.received || 0) + 
                   Number(details.purchase || 0) + 
                   Number(details.billed || 0) + 
                   Number(details.received_load || 0) +
                   Number(details.inward || 0);
    const outward = Number(details.sold || 0) + 
                    Number(details.sale || 0) + 
                    Number(details.despatched || 0) +
                    Number(details.desp || 0) +
                    Number(details.outward || 0);
    return opening + inward - outward;
};

/**
 * Aggregates all transactions for a specific level, entity, and date.
 * This "discovers" transactions that might have been missed by the real-time sync.
 */
const aggregateDayTransactions = async (level, entityId, date, clientId) => {
    const dailyDelta = {}; // sku -> { purchase/sold/received: qty }
    const normalizeSku = (s) => String(s || "").trim().toUpperCase();

    try {
        if (level === "cnf") {
            // 1. Inward: cnf_purchases
            const qIn = query(collection(db, "cnf_purchases"), where("clientId", "==", clientId), where("date", "==", date));
            const snapIn = await getDocs(qIn);
            snapIn.forEach(d => {
                const data = d.data();
                const sku = normalizeSku(data.sku);
                if (sku) {
                    if (!dailyDelta[sku]) dailyDelta[sku] = {};
                    dailyDelta[sku].purchase = (dailyDelta[sku].purchase || 0) + Number(data.qtyPkt || 0);
                }
            });

            // 2. Outward: distributor_invoices (Sales to Distributors)
            const qOut = query(collection(db, "distributor_invoices"), where("clientId", "==", clientId), where("approvedDateOnly", "==", date));
            const snapOut = await getDocs(qOut);
            snapOut.forEach(d => {
                const data = d.data();
                Object.entries(data.items || {}).forEach(([sku, qty]) => {
                    const skuUpper = normalizeSku(sku);
                    if (skuUpper) {
                        if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                        dailyDelta[skuUpper].sold = (dailyDelta[skuUpper].sold || 0) + Number(qty || 0);
                    }
                });
            });
        } 
        else if (level === "distributor") {
            // 1. Inward: distributor_invoices addressed to this distributor
            const qIn = query(collection(db, "distributor_invoices"), where("distributorId", "==", entityId), where("approvedDateOnly", "==", date));
            const snapIn = await getDocs(qIn);
            snapIn.forEach(d => {
                const data = d.data();
                Object.entries(data.items || {}).forEach(([sku, qty]) => {
                    const skuUpper = normalizeSku(sku);
                    if (skuUpper) {
                        if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                        dailyDelta[skuUpper].received = (dailyDelta[skuUpper].received || 0) + Number(qty || 0);
                    }
                });
            });

            // 2. Outward: orders where source is DISTRIBUTOR_TO_DS or DISTRIBUTOR_APP
            const qOut = query(collection(db, "orders"), where("distributorId", "==", entityId), where("approvedDateOnly", "==", date), where("status", "==", "APPROVED"));
            const snapOut = await getDocs(qOut);
            snapOut.forEach(d => {
                const data = d.data();
                if (data.source === "DISTRIBUTOR_TO_DS" || data.source === "DISTRIBUTOR_APP") {
                    Object.entries(data.items || {}).forEach(([sku, qty]) => {
                        const skuUpper = normalizeSku(sku);
                        if (skuUpper) {
                            if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                            dailyDelta[skuUpper].sold = (dailyDelta[skuUpper].sold || 0) + Number(qty || 0);
                        }
                    });
                }
            });
        }
        else if (level === "ds") {
            // 1. Inward: orders where source == DISTRIBUTOR_TO_DS and partyId == dsId
            const qIn = query(collection(db, "orders"), where("partyId", "==", entityId), where("source", "==", "DISTRIBUTOR_TO_DS"), where("approvedDateOnly", "==", date), where("status", "==", "APPROVED"));
            const snapIn = await getDocs(qIn);
            snapIn.forEach(d => {
                const data = d.data();
                Object.entries(data.items || {}).forEach(([sku, qty]) => {
                    const skuUpper = normalizeSku(sku);
                    if (skuUpper) {
                        if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                        dailyDelta[skuUpper].received = (dailyDelta[skuUpper].received || 0) + Number(qty || 0);
                    }
                });
            });

            // 2. Outward: Outlet_Orders (Mobile) AND orders (Web)
            const qOut1 = query(collection(db, "Outlet_Orders"), where("dsId", "==", entityId), where("approvedDateOnly", "==", date));
            const snapOut1 = await getDocs(qOut1);
            snapOut1.forEach(d => {
                const data = d.data();
                Object.entries(data.items || {}).forEach(([sku, qty]) => {
                    const skuUpper = normalizeSku(sku);
                    if (skuUpper) {
                        if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                        dailyDelta[skuUpper].sold = (dailyDelta[skuUpper].sold || 0) + Number(qty || 0);
                    }
                });
            });

            const qOut2 = query(collection(db, "orders"), where("dsId", "==", entityId), where("source", "==", "DS_TO_OUTLET"), where("approvedDateOnly", "==", date), where("status", "==", "APPROVED"));
            const snapOut2 = await getDocs(qOut2);
            snapOut2.forEach(d => {
                const data = d.data();
                Object.entries(data.items || {}).forEach(([sku, qty]) => {
                    const skuUpper = normalizeSku(sku);
                    if (skuUpper) {
                        if (!dailyDelta[skuUpper]) dailyDelta[skuUpper] = {};
                        dailyDelta[skuUpper].sold = (dailyDelta[skuUpper].sold || 0) + Number(qty || 0);
                    }
                });
            });
        }
    } catch (err) {
        console.error("Aggregation Error for " + level + " on " + date + ":", err);
    }

    return dailyDelta;
};

/**
 * Robustly retrieves stock for a given level and entity on a specific date.
 * If the document is missing or not finalized, it walks back to find the opening,
 * AND aggregates any existing transactions for that day to ensure accuracy.
 */
export const getOrInitStock = async (level, entityId, targetDate, clientId) => {
    const levelMap = {
        "cnf": { coll: "cnf_stock_daily", idField: "clientId", docId: `${clientId}_${targetDate}` },
        "distributor": { coll: "distributor_stock_daily", idField: "distributorId", docId: `${entityId}_${targetDate}` },
        "ds": { coll: "ds_stock_daily", idField: "dsId", docId: `${entityId}_${targetDate}` }
    };
    
    if (!levelMap[level]) throw new Error("Invalid stock level: " + level);
    const { coll, idField, docId } = levelMap[level];
    
    // 1. Check if finalized data already exists
    const stockRef = doc(db, coll, docId);
    const snap = await getDoc(stockRef);
    const existingData = snap.exists() ? snap.data() : null;
    
    // If it exists and is finalized with aggregated data, we return it
    const isBlank = !existingData?.inventory || Object.keys(existingData.inventory).length === 0;

    if (existingData && existingData.carryForwardDone) {
        // If it was marked as finalized but is actually blank, we DO NOT return early.
        // This handles documents that were initialized by previous versions of the script without the SKU loop.
        if (!isBlank) {
            return { id: snap.id, ...existingData };
        }
    }

    // 2. Need to initialize/finalize/repair. Find the most recent document BEFORE targetDate for opening
    const prevQuery = query(
        collection(db, coll),
        where(idField, "==", entityId),
        where("date", "<", targetDate),
        orderBy("date", "desc"),
        limit(1)
    );
    const lastSnap = await getDocs(prevQuery);
    
    let newInventory = {};
    if (!lastSnap.empty) {
        const lastData = lastSnap.docs[0].data();
        Object.entries(lastData.inventory || {}).forEach(([sku, info]) => {
            const closing = calculateClosing(info);
            const skuUpper = sku.trim().toUpperCase();
            if (closing !== 0) {
                newInventory[skuUpper] = { opening: closing };
            }
        });
    }

    // 3. New Discovery Sync: Aggregate any existing transactions for THIS date
    const aggregatedToday = await aggregateDayTransactions(level, entityId, targetDate, clientId);
    
    // 4. Ensure all SKUMaster SKUs are present (User Request: "flow all skumaster skus in all stock ledgers")
    const skuSnap = await getDocs(collection(db, "skuMaster"));
    skuSnap.forEach(d => {
        const data = d.data();
        const skuUpper = String(data.sku || data.Name || "").trim().toUpperCase();
        if (skuUpper && !newInventory[skuUpper]) {
            newInventory[skuUpper] = { opening: 0 };
        }
    });

    // Merge Discovery into newInventory
    Object.entries(aggregatedToday).forEach(([sku, fields]) => {
        if (!newInventory[sku]) newInventory[sku] = { opening: 0 };
        Object.assign(newInventory[sku], fields);
    });

    // 5. Atomically initialize/repair today's document
    return await runTransaction(db, async (transaction) => {
        const tSnap = await transaction.get(stockRef);
        const tExisting = tSnap.exists() ? tSnap.data() : null;

        // If it was already repaired/finalized by another process while we were querying, return it
        if (tExisting && tExisting.carryForwardDone && !isBlank) return tExisting;

        // If some real-time updates happened but weren't finalized, merge them too
        if (tExisting?.inventory) {
            Object.entries(tExisting.inventory).forEach(([sku, info]) => {
                const skuUpper = sku.trim().toUpperCase();
                if (!newInventory[skuUpper]) newInventory[skuUpper] = { opening: 0 };
                Object.keys(info).forEach(key => {
                    if (key !== "opening") {
                        // For transaction fields, we take the max of what we discovered and what's in the doc
                        newInventory[skuUpper][key] = Math.max((newInventory[skuUpper][key] || 0), (info[key] || 0));
                    }
                });
            });
        }

        const payload = {
            [idField]: entityId,
            date: targetDate,
            clientId: clientId || "pan",
            inventory: newInventory,
            carryForwardDone: true,
            updatedAt: serverTimestamp()
        };

        transaction.set(stockRef, payload, { merge: true });
        return { id: docId, ...payload };
    });
};
